import React, { useState, useEffect } from 'react';
import { Card, Select, Table, Button, Space, message, Modal, Form, Popconfirm, Alert, Input, Tag, Tooltip, DatePicker, Checkbox } from 'antd';
import { TrInputNumber as InputNumber } from './TrInputNumber';
import { DeleteOutlined, SaveOutlined, HistoryOutlined, BarcodeOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import dayjs from 'dayjs';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCanDelete } from '../hooks/useCanDelete';

interface Variant {
  id: number;
  name: string;
}

interface VariantValue {
  id: number;
  variantId: number;
  value: string;
  code?: string;
}

interface SelectedVariant {
  variantId: number;
  variantName: string;
  selectedValueIds: number[];
}

interface ProductBarcode {
  id: number;
  productId: number;
  productVariantId?: number;
  barcode: string;
  barcodeType: string;
  isPrimary: boolean;
}

interface ProductVariant {
  id?: number;
  sku: string;
  price: number;
  wholesalePrice?: number;
  purchasePrice?: number;
  stock: number;
  variantCombination: string;
  valueIds: number[];
  values?: any[];
  barcodes?: ProductBarcode[];
  primaryBarcode?: string;
}

interface ProductVariantSelectorProps {
  productId?: number;
  productCode?: string;
  isPriceVariant?: boolean;
  isWholesalePrice?: boolean;
  isPurchasePrice?: boolean;
}

const ProductVariantSelector: React.FC<ProductVariantSelectorProps> = ({ 
  productId,
  productCode,
  isPriceVariant = false,
  isWholesalePrice = false,
  isPurchasePrice = false
}) => {
  const canDelete = useCanDelete();
  const [variants, setVariants] = useState<Variant[]>([]);
  const [variantValues, setVariantValues] = useState<Record<number, VariantValue[]>>({});
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariant[]>([]);
  const [previewVariants, setPreviewVariants] = useState<ProductVariant[]>([]);
  const [savedVariants, setSavedVariants] = useState<ProductVariant[]>([]);
  const [loading, setLoading] = useState(false);

  // Barkod state'leri
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const [barcodeHistoryModalVisible, setBarcodeHistoryModalVisible] = useState(false);
  const [selectedVariantForBarcode, setSelectedVariantForBarcode] = useState<ProductVariant | null>(null);
  const [barcodeHistoryList, setBarcodeHistoryList] = useState<ProductBarcode[]>([]);
  const [newBarcode, setNewBarcode] = useState('');

  // Fiyat state'leri
  const [priceHistoryModalVisible, setPriceHistoryModalVisible] = useState(false);
  const [priceHistoryData, setPriceHistoryData] = useState<any[]>([]);
  const [selectedPriceType, setSelectedPriceType] = useState<'retail' | 'wholesale' | 'purchase'>('retail');
  const [addPriceModalVisible, setAddPriceModalVisible] = useState(false);
  const [selectedVariantForPrice, setSelectedVariantForPrice] = useState<ProductVariant | null>(null);
  const [selectedPriceTypeId, setSelectedPriceTypeId] = useState<number>(1);
  const [priceForm] = Form.useForm();
  
  // Transfer modals
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState<ProductVariant | null>(null);
  const [transferTargetId, setTransferTargetId] = useState<number | null>(null);
  const [remainingVariantsCount, setRemainingVariantsCount] = useState(0);
  
  // Stock distribution modal
  const [distributionModalVisible, setDistributionModalVisible] = useState(false);
  const [mainProductStock, setMainProductStock] = useState(0);
  const [stockDistributions, setStockDistributions] = useState<Record<number, number>>({});
  const { baseCurrencySymbol } = useCurrency();

  useEffect(() => {
    loadVariants();
  }, []);

  useEffect(() => {
    if (productId) {
      loadSavedVariants();
    }
  }, [productId]);

  useEffect(() => {
    generatePreview();
  }, [selectedVariants]);

  const loadVariants = async () => {
    try {
      const response = await api.get('/variants');
      setVariants(response.data);
      
      // Her varyant için değerlerini yükle
      const valuesPromises = response.data.map((v: Variant) => 
        api.get(`/variants/values/${v.id}`)
      );
      const valuesResponses = await Promise.all(valuesPromises);
      
      const valuesMap: Record<number, VariantValue[]> = {};
      response.data.forEach((v: Variant, index: number) => {
        valuesMap[v.id] = valuesResponses[index].data;
      });
      setVariantValues(valuesMap);
    } catch (error) {
      console.error('Varyantlar yüklenirken hata:', error);
      message.error('Varyantlar yüklenemedi');
    }
  };

  const loadSavedVariants = async () => {
    if (!productId) return;
    
    try {
      const response = await api.get(`/productvariants/by-product/${productId}`);
      
      // Her varyant için barkodları yükle
      const variantsWithBarcodes = await Promise.all(
        response.data.map(async (variant: ProductVariant) => {
          if (variant.id) {
            try {
              const barcodes = await loadVariantBarcodes(variant.id);
              // Son eklenen barkodu göster (en yüksek ID = en son eklenen)
              const latestBarcode = barcodes.length > 0 
                ? barcodes.sort((a: ProductBarcode, b: ProductBarcode) => b.id - a.id)[0]?.barcode || ''
                : '';
              return { ...variant, barcodes, primaryBarcode: latestBarcode };
            } catch {
              return { ...variant, barcodes: [], primaryBarcode: '' };
            }
          }
          return variant;
        })
      );
      
      setSavedVariants(variantsWithBarcodes);
      console.log('✅ Kaydedilmiş varyantlar:', variantsWithBarcodes);
    } catch (error) {
      console.error('Kaydedilmiş varyantlar yüklenirken hata:', error);
    }
  };

  const handleVariantSelect = (variantId: number) => {
    const variant = variants.find(v => v.id === variantId);
    if (!variant) return;

    const exists = selectedVariants.find(sv => sv.variantId === variantId);
    if (exists) {
      message.warning('Bu varyant zaten seçilmiş');
      return;
    }

    setSelectedVariants([...selectedVariants, {
      variantId: variant.id,
      variantName: variant.name,
      selectedValueIds: []
    }]);
  };

  const handleVariantValueSelect = (variantId: number, valueIds: number[]) => {
    setSelectedVariants(prev => prev.map(sv => 
      sv.variantId === variantId 
        ? { ...sv, selectedValueIds: valueIds }
        : sv
    ));
  };

  const handleRemoveVariant = (variantId: number) => {
    setSelectedVariants(prev => prev.filter(sv => sv.variantId !== variantId));
  };

  // ================================================================================
  // FİYAT FONKSİYONLARI
  // ================================================================================

  const handleOpenAddPriceModal = (variant: ProductVariant, priceType: 'retail' | 'wholesale' | 'purchase') => {
    if (!variant.id) return;

    let priceTypeId = 1; // Perakende
    if (priceType === 'wholesale') priceTypeId = 2;
    if (priceType === 'purchase') priceTypeId = 3;

    setSelectedVariantForPrice(variant);
    setSelectedPriceTypeId(priceTypeId);
    setSelectedPriceType(priceType);
    
    // Form'u temizle ve varsayılan değerleri set et
    priceForm.setFieldsValue({
      price: variant.price || 0,
      validFrom: dayjs(),
      validTo: null,
      isIndefinite: true
    });
    
    setAddPriceModalVisible(true);
  };

  const handleAddPrice = async () => {
    try {
      const values = await priceForm.validateFields();
      setLoading(true);

      await api.post('/productprices', {
        productId: productId,
        productVariantId: selectedVariantForPrice?.id,
        priceTypeId: selectedPriceTypeId,
        price: values.price,
        validFrom: values.validFrom?.toISOString() || new Date().toISOString(),
        validTo: values.isIndefinite ? null : values.validTo?.toISOString(),
        isIndefinite: values.isIndefinite
      });

      message.success('Fiyat eklendi');
      setAddPriceModalVisible(false);
      priceForm.resetFields();
      await loadSavedVariants();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Lütfen tüm alanları doldurun');
      } else {
        console.error('Fiyat ekleme hatası:', error);
        message.error(error.response?.data?.message || 'Fiyat eklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShowPriceHistory = async (variant: ProductVariant, priceType: 'retail' | 'wholesale' | 'purchase') => {
    if (!variant.id) return;

    try {
      setLoading(true);
      let priceTypeId = 1; // Perakende
      if (priceType === 'wholesale') priceTypeId = 2;
      if (priceType === 'purchase') priceTypeId = 3;

      const { data } = await api.get(`/productprices/variant/${variant.id}/history`);
      
      // İstenen priceType'a göre filtrele
      const filteredData = data.filter((item: any) => item.priceTypeId === priceTypeId);
      
      setPriceHistoryData(filteredData);
      setSelectedPriceType(priceType);
      setSelectedVariantForBarcode(variant);
      setPriceHistoryModalVisible(true);
    } catch (error: any) {
      console.error('Fiyat geçmişi yükleme hatası:', error);
      message.error(error.response?.data?.message || 'Fiyat geçmişi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // ================================================================================
  // BARKOD FONKSİYONLARI
  // ================================================================================

  const loadVariantBarcodes = async (variantId: number) => {
    try {
      const { data } = await api.get(`/productbarcodes/variant/${variantId}`);
      return data;
    } catch (error) {
      console.error('Barkod yüklenirken hata:', error);
      return [];
    }
  };

  const handleGenerateBarcode = async (variant: ProductVariant) => {
    if (!variant.id) {
      message.warning('Lütfen önce varyantı kaydedin');
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post('/productbarcodes/generate', {
        productId: productId,
        productVariantId: variant.id
      });
      message.success('Barkod oluşturuldu');
      await loadSavedVariants(); // Yenile
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Barkod oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleShowBarcodeHistory = async (variant: ProductVariant) => {
    if (!variant.id) return;

    try {
      const barcodes = await loadVariantBarcodes(variant.id);
      setBarcodeHistoryList(barcodes);
      setSelectedVariantForBarcode(variant);
      setBarcodeHistoryModalVisible(true);
    } catch (error) {
      message.error('Barkod geçmişi yüklenemedi');
    }
  };

  const handleOpenBarcodeModal = (variant: ProductVariant) => {
    setSelectedVariantForBarcode(variant);
    setNewBarcode('');
    setBarcodeModalVisible(true);
  };

  const generateEAN13Barcode = () => {
    // EAN-13 barkod üret (12 digit + 1 check digit)
    const timestamp = Date.now().toString().slice(-9); // Son 9 digit
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0'); // 3 random digit
    const first12 = (timestamp + random).slice(0, 12);
    
    // Check digit hesapla (EAN-13 algoritması)
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(first12[i]);
      sum += (i % 2 === 0) ? digit : digit * 3;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    
    return first12 + checkDigit;
  };

  const handleGenerateBarcodeInModal = () => {
    if (!selectedVariantForBarcode?.id) return;
    
    const newBarcodeValue = generateEAN13Barcode();
    setNewBarcode(newBarcodeValue);
    message.success('Barkod üretildi');
  };

  const handleAddManualBarcode = async () => {
    if (!selectedVariantForBarcode?.id || !newBarcode) {
      message.warning('Lütfen barkod girin');
      return;
    }

    try {
      setLoading(true);
      await api.post('/productbarcodes', {
        productId: productId,
        productVariantId: selectedVariantForBarcode.id,
        barcode: newBarcode,
        barcodeType: 'MANUAL',
        isPrimary: !selectedVariantForBarcode.barcodes || selectedVariantForBarcode.barcodes.length === 0
      });
      message.success('Barkod eklendi');
      setBarcodeModalVisible(false);
      setNewBarcode('');
      await loadSavedVariants();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Barkod eklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerateBarcodes = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      await api.post(`/productbarcodes/bulk-generate/${productId}`);
      message.success('Tüm varyantlar için barkod oluşturuldu');
      await loadSavedVariants();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Barkod oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  // ================================================================================

  const generatePreview = () => {
    // Tüm varyantlar için değer seçimi yapılmış mı kontrol et
    const allSelected = selectedVariants.every(sv => sv.selectedValueIds.length > 0);
    if (!allSelected || selectedVariants.length === 0) {
      setPreviewVariants([]);
      return;
    }

    // Kombinasyonları oluştur
    const combinations: ProductVariant[] = [];
    
    const generate = (index: number, current: number[], currentNames: string[]) => {
      if (index === selectedVariants.length) {
        // Kombinasyon oluştur
        const variantName = currentNames.join(' / ');
        combinations.push({
          sku: 'Otomatik oluşturulacak',
          price: 0,
          stock: 0,
          variantCombination: variantName,
          valueIds: [...current]
        });
        return;
      }

      const sv = selectedVariants[index];
      sv.selectedValueIds.forEach(valueId => {
        const value = variantValues[sv.variantId]?.find(vv => vv.id === valueId);
        if (value) {
          generate(index + 1, [...current, valueId], [...currentNames, value.value]);
        }
      });
    };

    generate(0, [], []);
    setPreviewVariants(combinations);
  };

  const handleSaveVariants = async () => {
    if (!productId || previewVariants.length === 0) {
      message.warning('Lütfen varyant seçimi yapın');
      return;
    }

    // Önce ana ürün stoğunu kontrol et
    try {
      console.log('🔍 Stok kontrolü yapılıyor, productId:', productId);
      const stockResponse = await api.get(`/productvariants/main-product-stock/${productId}`);
      const mainStock = stockResponse.data.totalStock;
      console.log('📦 Ana ürün stoğu:', mainStock);

      if (mainStock > 0) {
        // Stok varsa, dağıtım modal'ını aç
        console.log('✅ Stok var! Modal açılıyor...');
        setMainProductStock(mainStock);
        setDistributionModalVisible(true);
        return;
      } else {
        console.log('⚠️ Stok yok, direkt kaydediliyor');
      }
    } catch (error) {
      console.error('❌ Stok kontrolü hatası:', error);
    }

    // Stok yoksa direkt kaydet
    await saveVariantsDirectly();
  };

  const saveVariantsDirectly = async () => {
    setLoading(true);
    try {
      let successCount = 0;
      
      for (const variant of previewVariants) {
        const payload = {
          productId: productId,
          price: variant.price,
          variantValueIds: variant.valueIds
        };

        await api.post('/productvariants', payload);
        successCount++;
      }

      message.success(`${successCount} varyant başarıyla kaydedildi`);
      
      setSelectedVariants([]);
      setPreviewVariants([]);
      await loadSavedVariants();
      
    } catch (error: any) {
      console.error('Varyant kaydetme hatası:', error);
      const errorMsg = error.response?.data?.message || 'Varyantlar kaydedilemedi';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDistributeStock = async () => {
    // Önce varyantları kaydet
    setLoading(true);
    try {
      console.log('🔄 Varyantlar kaydediliyor...');
      const createdVariants: any[] = [];
      
      for (const variant of previewVariants) {
        const payload = {
          productId: productId,
          price: variant.price,
          variantValueIds: variant.valueIds
        };

        const response = await api.post('/productvariants', payload);
        createdVariants.push(response.data);
        console.log('✅ Varyant oluşturuldu:', response.data);
      }

      // Toplam dağıtımı kontrol et
      const totalDistribution = Object.values(stockDistributions).reduce((a, b) => a + b, 0);
      console.log(`📊 Toplam dağıtım: ${totalDistribution}, Ana ürün stoğu: ${mainProductStock}`);
      
      if (totalDistribution !== mainProductStock) {
        console.error('❌ Dağıtım toplamı eşit değil!');
        message.error(`Toplam dağıtım (${totalDistribution}) ana ürün stoğuna (${mainProductStock}) eşit olmalı`);
        setLoading(false);
        return;
      }

      // Stok dağıtımını yap
      const distributionPayload = {
        productId: productId,
        distributions: createdVariants.map((v, index) => ({
          variantId: v.id,
          quantity: stockDistributions[index] || 0
        })).filter(d => d.quantity > 0)
      };

      console.log('📤 Stok dağıtım isteği gönderiliyor:', distributionPayload);
      const distributeResponse = await api.post('/productvariants/distribute-stock', distributionPayload);
      console.log('✅ Stok dağıtım yanıtı:', distributeResponse.data);
      
      message.success('Varyantlar kaydedildi ve stok dağıtıldı');
      
      setSelectedVariants([]);
      setPreviewVariants([]);
      setDistributionModalVisible(false);
      setStockDistributions({});
      console.log('🔄 Kaydedilmiş varyantlar yeniden yükleniyor...');
      await loadSavedVariants();
      console.log('✅ Tüm işlem tamamlandı!');
      
    } catch (error: any) {
      console.error('❌ Stok dağıtım hatası:', error);
      console.error('❌ Hata detayı:', error.response?.data);
      const errorMsg = error.response?.data?.message || 'Stok dağıtımı başarısız';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVariant = async (variant: ProductVariant) => {
    if (!variant.id) return;

    try {
      const response = await api.delete(`/productvariants/${variant.id}`);
      
      // Eğer stok varsa ve transfer gerekiyorsa
      if (response.data?.requiresStockTransfer) {
        setVariantToDelete(variant);
        setRemainingVariantsCount(response.data.remainingVariantsCount);
        setDeleteModalVisible(true);
        return;
      }

      message.success('Varyant silindi');
      await loadSavedVariants();
      
    } catch (error: any) {
      console.error('Varyant silme hatası:', error);
      
      // Backend'den gelen hata kontrolü
      if (error.response?.data?.requiresStockTransfer) {
        setVariantToDelete(variant);
        setRemainingVariantsCount(error.response.data.remainingVariantsCount);
        setDeleteModalVisible(true);
      } else {
        const errorMsg = error.response?.data?.message || 'Varyant silinemedi';
        message.error(errorMsg);
      }
    }
  };

  const handleTransferAndDelete = async () => {
    if (!variantToDelete?.id) return;

    console.log('🔄 handleTransferAndDelete çağrıldı');
    console.log('   variantToDelete:', variantToDelete);
    console.log('   remainingVariantsCount:', remainingVariantsCount);
    console.log('   transferTargetId:', transferTargetId);

    try {
      if (remainingVariantsCount === 0) {
        // Son varyant - ana ürüne transfer et
        console.log('➡️ ANA ÜRÜNE TRANSFER EDİLİYOR');
        await api.post(`/productvariants/${variantToDelete.id}/transfer-to-main`);
        message.success('Stok ana ürüne transfer edildi ve varyant silindi');
      } else {
        // Başka varyanta transfer et
        console.log('➡️ VARYANTA TRANSFER EDİLİYOR');
        if (!transferTargetId) {
          console.error('❌ transferTargetId yok!');
          message.warning('Lütfen hedef varyant seçin');
          return;
        }
        console.log(`   Endpoint: /productvariants/${variantToDelete.id}/transfer-to-variant`);
        console.log(`   Payload: { targetVariantId: ${transferTargetId} }`);
        await api.post(`/productvariants/${variantToDelete.id}/transfer-to-variant`, {
          targetVariantId: transferTargetId
        });
        message.success('Stok transfer edildi ve varyant silindi');
      }

      setDeleteModalVisible(false);
      setVariantToDelete(null);
      setTransferTargetId(null);
      await loadSavedVariants();
      
    } catch (error: any) {
      console.error('Transfer hatası:', error);
      const errorMsg = error.response?.data?.message || 'Stok transferi başarısız';
      message.error(errorMsg);
    }
  };

  const handlePriceChange = (index: number, price: number) => {
    setPreviewVariants(prev => prev.map((v, i) => 
      i === index ? { ...v, price } : v
    ));
  };

  const previewColumns = [
    {
      title: 'Varyant Kombinasyonu',
      dataIndex: 'variantCombination',
      key: 'variantCombination'},
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: () => <span style={{ color: '#999' }}>Otomatik oluşturulacak</span>
    },
    {
      title: 'Fiyat',
      key: 'price',
      render: (_: any, record: ProductVariant, index: number) => (
        <InputNumber
          min={0}
          value={record.price}
          onChange={(value) => handlePriceChange(index, value || 0)}
          style={{ width: '100%' }}
          placeholder="Fiyat"
        />
      )},
  ];

  const savedColumns = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 150},
    {
      title: 'Varyant',
      key: 'variant',
      render: (_: any, record: ProductVariant) => {
        if (record.values && record.values.length > 0) {
          return record.values.map((v: any) => v.variantValue).join(' / ');
        }
        return record.variantCombination || '-';
      }
    },
    {
      title: 'Barkod',
      key: 'barcode',
      width: 200,
      render: (_: any, record: ProductVariant) => {
        if (!record.id) return '-';
        
        if (!record.primaryBarcode) {
          return (
            <Button 
              size="small" 
              type="link"
              icon={<ThunderboltOutlined />}
              onClick={() => handleGenerateBarcode(record)}
              loading={loading}
            >
              Otomatik Üret
            </Button>
          );
        }

        return (
          <Space>
            <Tooltip title="Barkoda tıklayarak düzenle">
              <Tag 
                color="blue" 
                style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                onClick={() => handleOpenBarcodeModal(record)}
              >
                {record.primaryBarcode}
              </Tag>
            </Tooltip>
            {record.barcodes && record.barcodes.length > 1 && (
              <Tooltip title="Geçmiş barkodları göster">
                <Button 
                  size="small" 
                  type="text"
                  icon={<HistoryOutlined />}
                  onClick={() => handleShowBarcodeHistory(record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Perakende Fiyat',
      dataIndex: 'price',
      key: 'price',
      width: 150,
      render: (price: number, record: ProductVariant) => (
        <Space>
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{baseCurrencySymbol}{price.toFixed(2)}</span>
          <Tooltip title="Yeni fiyat ekle">
            <Button 
              size="small" 
              type="text"
              icon={<PlusOutlined />}
              onClick={() => handleOpenAddPriceModal(record, 'retail')}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          <Tooltip title="Fiyat geçmişi">
            <Button 
              size="small" 
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleShowPriceHistory(record, 'retail')}
            />
          </Tooltip>
        </Space>
      )
    },
    ...(isWholesalePrice ? [{
      title: 'Toptan Fiyat',
      key: 'wholesalePrice',
      width: 150,
      render: (_: any, record: ProductVariant) => (
        <Space>
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{baseCurrencySymbol}{(record.wholesalePrice || 0).toFixed(2)}</span>
          <Tooltip title="Yeni fiyat ekle">
            <Button 
              size="small" 
              type="text"
              icon={<PlusOutlined />}
              onClick={() => handleOpenAddPriceModal(record, 'wholesale')}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          <Tooltip title="Fiyat geçmişi">
            <Button 
              size="small" 
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleShowPriceHistory(record, 'wholesale')}
            />
          </Tooltip>
        </Space>
      )
    }] : []),
    ...(isPurchasePrice ? [{
      title: 'Alış Fiyatı',
      key: 'purchasePrice',
      width: 150,
      render: (_: any, record: ProductVariant) => (
        <Space>
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{baseCurrencySymbol}{(record.purchasePrice || 0).toFixed(2)}</span>
          <Tooltip title="Yeni fiyat ekle">
            <Button 
              size="small" 
              type="text"
              icon={<PlusOutlined />}
              onClick={() => handleOpenAddPriceModal(record, 'purchase')}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          <Tooltip title="Fiyat geçmişi">
            <Button 
              size="small" 
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleShowPriceHistory(record, 'purchase')}
            />
          </Tooltip>
        </Space>
      )
    }] : []),
    {
      title: 'Stok',
      dataIndex: 'stock',
      key: 'stock',
      width: 80},
    {
      title: 'İşlemler',
      key: 'actions',
      width: 100,
      render: (_: any, record: ProductVariant) =>
        canDelete ? (
          <Popconfirm
            title="Bu varyantı silmek istediğinize emin misiniz?"
            onConfirm={() => handleDeleteVariant(record)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        ) : null},
  ];

  return (
    <div>
      <Card title="Varyant Seçimi" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <label>Varyant Ekle:</label>
            <Select
              style={{ width: 300, marginLeft: 8 }}
              placeholder="Varyant seçin"
              onChange={handleVariantSelect}
              value={null}
            >
              {variants
                .filter(v => !selectedVariants.find(sv => sv.variantId === v.id))
                .map(v => (
                  <Select.Option key={v.id} value={v.id}>
                    {v.name}
                  </Select.Option>
                ))}
            </Select>
          </div>

          {selectedVariants.map(sv => (
            <div key={sv.variantId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ minWidth: 100 }}>{sv.variantName}:</span>
              <Select
                mode="multiple"
                style={{ flex: 1 }}
                placeholder="Değer seçin"
                value={sv.selectedValueIds}
                onChange={(values) => handleVariantValueSelect(sv.variantId, values)}
              >
                {variantValues[sv.variantId]?.map(vv => (
                  <Select.Option key={vv.id} value={vv.id}>
                    {vv.value}
                  </Select.Option>
                ))}
              </Select>
              {canDelete && (
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveVariant(sv.variantId)}
                />
              )}
            </div>
          ))}
        </Space>
      </Card>

      {previewVariants.length > 0 && (
        <Card 
          title="Oluşturulacak Varyantlar" 
          style={{ marginBottom: 16 }}
          extra={
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              onClick={handleSaveVariants}
              loading={loading}
            >
              Varyantları Kaydet
            </Button>
          }
        >
          <Table
            dataSource={previewVariants}
            columns={previewColumns}
            rowKey={(record, index) => `preview-${index}`}
            pagination={false}
          />
        </Card>
      )}

      {savedVariants.length > 0 && (
        <Card title="Kaydedilmiş Varyantlar">
          <Table
            dataSource={savedVariants}
            columns={savedColumns}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}

      {/* Transfer Modal */}
      <Modal
        title="Stok Transferi Gerekli"
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setVariantToDelete(null);
          setTransferTargetId(null);
        }}
        onOk={handleTransferAndDelete}
        okText="Transfer Et ve Sil"
        cancelText="İptal"
      >
        <Alert
          message={`Bu varyantın stoğu var. ${remainingVariantsCount === 0 ? 'Ana ürüne' : 'Başka bir varyanta'} transfer edilecek.`}
          type="warning"
          style={{ marginBottom: 16 }}
        />

        {remainingVariantsCount > 0 && (
          <Form layout="vertical">
            <Form.Item label="Hedef Varyant" required>
              <Select
                placeholder="Varyant seçin"
                value={transferTargetId}
                onChange={setTransferTargetId}
              >
                {savedVariants
                  .filter(v => v.id !== variantToDelete?.id)
                  .map(v => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.sku} - {v.variantCombination}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Stock Distribution Modal */}
      <Modal
        title="Ana Ürün Stoğunu Varyantlara Dağıt"
        open={distributionModalVisible}
        onCancel={() => {
          setDistributionModalVisible(false);
          setStockDistributions({});
        }}
        onOk={handleDistributeStock}
        okText="Dağıt ve Kaydet"
        cancelText="İptal"
        width={700}
      >
        <Alert
          message={`Ana üründe ${mainProductStock} adet stok var. Bu stoğu varyantlara dağıtın.`}
          type="info"
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <strong>Toplam Dağıtım: </strong>
          <span style={{ 
            color: Object.values(stockDistributions).reduce((a, b) => a + b, 0) === mainProductStock ? 'green' : 'red',
            fontWeight: 'bold'
          }}>
            {Object.values(stockDistributions).reduce((a, b) => a + b, 0)} / {mainProductStock}
          </span>
        </div>

        <Table
          dataSource={previewVariants}
          columns={[
            {
              title: 'Varyant',
              dataIndex: 'variantCombination',
              key: 'variantCombination'},
            {
              title: 'Miktar',
              key: 'quantity',
              render: (_: any, record: ProductVariant, index: number) => (
                <InputNumber
                  min={0}
                  max={mainProductStock}
                  value={stockDistributions[index] || 0}
                  onChange={(value) => {
                    setStockDistributions(prev => ({
                      ...prev,
                      [index]: value || 0
                    }));
                  }}
                  style={{ width: '100%' }}
                />
              )},
          ]}
          rowKey={(record, index) => `dist-${index}`}
          pagination={false}
          size="small"
        />
      </Modal>

      {/* Manuel Barkod Ekleme Modal */}
      <Modal
        title="Barkod Ekle"
        open={barcodeModalVisible}
        onCancel={() => {
          setBarcodeModalVisible(false);
          setNewBarcode('');
        }}
        onOk={handleAddManualBarcode}
        okText="Ekle"
        cancelText="İptal"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message={`Varyant: ${selectedVariantForBarcode?.sku || ''}`}
            type="info"
            style={{ marginBottom: 16 }}
          />
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Barkod numarası"
              value={newBarcode}
              onChange={(e) => setNewBarcode(e.target.value)}
              prefix={<BarcodeOutlined />}
              onPressEnter={handleAddManualBarcode}
              autoFocus
            />
            <Tooltip title="Otomatik barkod üret">
              <Button
                icon={<ThunderboltOutlined />}
                onClick={handleGenerateBarcodeInModal}
                type="primary"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Üret
              </Button>
            </Tooltip>
          </Space.Compact>
        </Space>
      </Modal>

      {/* Barkod Geçmişi Modal */}
      <Modal
        title="Barkod Geçmişi"
        open={barcodeHistoryModalVisible}
        onCancel={() => setBarcodeHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setBarcodeHistoryModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={600}
      >
        <Alert
          message={`Varyant: ${selectedVariantForBarcode?.sku || ''}`}
          type="info"
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={barcodeHistoryList}
          columns={[
            {
              title: 'Barkod',
              dataIndex: 'barcode',
              key: 'barcode',
              render: (text: string) => (
                <Tag color="blue" style={{ fontFamily: 'monospace' }}>
                  {text}
                </Tag>
              )
            },
            {
              title: 'Tip',
              dataIndex: 'barcodeType',
              key: 'barcodeType'},
            {
              title: 'Birincil',
              dataIndex: 'isPrimary',
              key: 'isPrimary',
              render: (isPrimary: boolean) => (
                isPrimary ? <Tag color="green">Evet</Tag> : <Tag>Hayır</Tag>
              )
            }
          ]}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Modal>

      {/* Fiyat Ekle Modal */}
      <Modal
        title={`Yeni Fiyat Ekle - ${selectedPriceType === 'retail' ? 'Perakende' : selectedPriceType === 'wholesale' ? 'Toptan' : 'Alış'}`}
        open={addPriceModalVisible}
        onCancel={() => {
          setAddPriceModalVisible(false);
          priceForm.resetFields();
        }}
        onOk={handleAddPrice}
        okText="Kaydet"
        cancelText="İptal"
        confirmLoading={loading}
        width={500}
      >
        <Alert
          message={`Varyant: ${selectedVariantForPrice?.sku || ''}`}
          type="info"
          style={{ marginBottom: 16 }}
        />
        <Form
          form={priceForm}
          layout="vertical"
          initialValues={{
            isIndefinite: true,
            validFrom: dayjs()
          }}
        >
          <Form.Item
            label="Fiyat"
            name="price"
            rules={[{ required: true, message: 'Lütfen fiyat girin' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              prefix={baseCurrencySymbol}
              placeholder="0.00"
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="Başlangıç Tarihi"
            name="validFrom"
            rules={[{ required: true, message: 'Lütfen başlangıç tarihi seçin' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Tarih seçin"
            />
          </Form.Item>

          <Form.Item
            name="isIndefinite"
            valuePropName="checked"
          >
            <Checkbox>
              Süresiz (Bitiş tarihi yok)
            </Checkbox>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.isIndefinite !== currentValues.isIndefinite
            }
          >
            {({ getFieldValue }) => 
              !getFieldValue('isIndefinite') && (
                <Form.Item
                  label="Bitiş Tarihi"
                  name="validTo"
                  rules={[
                    { required: !getFieldValue('isIndefinite'), message: 'Lütfen bitiş tarihi seçin' }
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Tarih seçin"
                  />
                </Form.Item>
              )
            }
          </Form.Item>
        </Form>
      </Modal>

      {/* Fiyat Geçmişi Modal */}
      <Modal
        title={`Fiyat Geçmişi - ${selectedPriceType === 'retail' ? 'Perakende' : selectedPriceType === 'wholesale' ? 'Toptan' : 'Alış'}`}
        open={priceHistoryModalVisible}
        onCancel={() => setPriceHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPriceHistoryModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={700}
      >
        <Alert
          message={`Varyant: ${selectedVariantForBarcode?.sku || ''}`}
          type="info"
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={priceHistoryData}
          columns={[
            {
              title: 'Fiyat',
              dataIndex: 'price',
              key: 'price',
              render: (price: number) => (
                <Tag color="green" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {baseCurrencySymbol}{price.toFixed(2)}
                </Tag>
              )
            },
            {
              title: 'Fiyat Tipi',
              dataIndex: 'priceTypeName',
              key: 'priceTypeName'},
            {
              title: 'Geçerlilik Başlangıcı',
              dataIndex: 'validFrom',
              key: 'validFrom',
              render: (date: string) => new Date(date).toLocaleDateString('tr-TR', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })
            },
            {
              title: 'Geçerlilik Bitişi',
              dataIndex: 'validTo',
              key: 'validTo',
              render: (date: string) => date ? new Date(date).toLocaleDateString('tr-TR', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              }) : <Tag color="blue">Süresiz</Tag>
            },
            {
              title: 'Oluşturulma',
              dataIndex: 'createdAt',
              key: 'createdAt',
              render: (date: string) => new Date(date).toLocaleDateString('tr-TR', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            }
          ]}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default ProductVariantSelector;
